import { Lock } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useAuth } from '@/hooks/use-auth'

interface FeatureBlockedModalProps {
  isOpen: boolean
  onClose: () => void
  featureName: string
  onUpgrade: () => void
}

export function FeatureBlockedModal({
  isOpen,
  onClose,
  featureName,
  onUpgrade,
}: FeatureBlockedModalProps) {
  const { user } = useAuth()
  const isSchoolStaff = user?.school_id && user?.primary_role !== 'school_admin' && user?.primary_role !== 'admin'
  const isTeacher = user?.primary_role === 'teacher'

  const premiumFeatures = isTeacher
    ? [
        '• Unlimited AI lesson plan generation',
        '• AI quiz & rubric creation',
        '• AI differentiation assistant',
        '• Advanced class analytics',
        '• Priority support',
      ]
    : [
        '• Unlimited AI Tutor — 24/7',
        '• Unlimited exam prep & practice',
        '• AI-generated study plans',
        '• Advanced analytics & insights',
        '• Priority support',
      ]

  const recommendedPlan = isTeacher ? 'Teacher Pro' : 'Student Pro'

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center justify-center mb-4">
            <div className="rounded-full bg-primary-100 dark:bg-primary-900/30 p-3">
              <Lock className="h-8 w-8 text-primary-600 dark:text-primary-400" />
            </div>
          </div>
          <DialogTitle className="text-center text-xl">
            Usage Limit Reached
          </DialogTitle>
          <DialogDescription className="text-center">
            You've reached your free limit for <strong>{featureName.replace(/_/g, ' ')}</strong>.
            {isSchoolStaff
              ? ' Please contact your school administrator to upgrade the school plan.'
              : ` Upgrade to ${recommendedPlan} for unlimited access.`}
          </DialogDescription>
        </DialogHeader>

        {!isSchoolStaff && (
          <div className="bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800/30 rounded-lg p-4 my-4">
            <p className="text-sm font-medium text-primary-900 dark:text-primary-200 mb-2">
              ✨ What you'll get with {recommendedPlan}:
            </p>
            <ul className="text-sm text-primary-800 dark:text-primary-300 space-y-1">
              {premiumFeatures.map((f, i) => <li key={i}>{f}</li>)}
            </ul>
          </div>
        )}

        <div className="flex gap-3">
          <Button variant="outline" onClick={onClose} className="flex-1">
            Maybe Later
          </Button>
          {isSchoolStaff ? (
            <Button onClick={onClose} className="flex-1">
              Got it
            </Button>
          ) : (
            <Button
              onClick={onUpgrade}
              className="flex-1 bg-gradient-to-r from-primary-500 to-secondary-500 hover:from-primary-600 hover:to-secondary-600 text-white"
            >
              Upgrade to {recommendedPlan}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
